import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, ClockCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const SearchHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [user]);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/weather/history`,
        { withCredentials: true }
      );
      setHistory(data);
    } catch (error) {
      toast.error('Failed to load search history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>Search History</h1>
          <p className="text-sm text-slate-600 mt-2">Review your previous weather queries</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-sm text-slate-600 font-mono">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 p-12 text-center">
            <ClockCounterClockwise size={48} weight="bold" className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>No searches yet</h3>
            <p className="text-sm text-slate-600 mb-6">Your weather lookup history will appear here</p>
            <Link to="/lookup" className="inline-block px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium">
              Make Your First Search
            </Link>
          </div>
        ) : (
          <div data-testid="search-history-list" className="space-y-4">
            {history.map((item, index) => (
              <div key={index} className="bg-white border border-slate-200 p-6 hover:border-blue-600 transition-colors">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Location</p>
                    <p className="text-sm font-medium text-slate-950">{item.location}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Date</p>
                    <p className="text-sm font-mono text-slate-950">{item.date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Time</p>
                    <p className="text-sm font-mono text-slate-950">{item.time}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Temperature</p>
                    <p className="text-sm font-mono font-bold text-slate-950">
                      {item.response_snapshot?.weather_data?.temp || 'N/A'}°F
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchHistory;
